import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, ArrowLeft, Globe, Heart, Twitter, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const TestimonialsPage = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // SEO
  useEffect(() => {
    document.title = t.testimonialsPage.seoTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", t.testimonialsPage.seoDescription);
    return () => {
      document.title = "Fondation Cœur Solidaire";
    };
  }, [t]);

  useEffect(() => {
    api
      .get("/testimonials")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Compute country list with counts
  const countries = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const c = (it.country || "").trim();
      if (!c) return;
      map.set(c, (map.get(c) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => (it.country || "").trim() === filter);
  }, [items, filter]);

  const goToDonate = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("donate");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7]" data-testid="testimonials-page">
      <Seo
        title={t.testimonialsPage.seoTitle}
        description={t.testimonialsPage.seoDescription}
        path={lang === "en" ? "/testimonials" : "/temoignages"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#002B49] text-white relative overflow-hidden">
        <div className="container-narrow relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors mb-8"
            data-testid="testimonials-back-home"
          >
            <ArrowLeft size={16} /> {t.testimonialsPage.backHome}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="eyebrow text-[#E8C77E]" data-testid="testimonials-eyebrow">
              {t.testimonialsPage.eyebrow}
            </span>
            <span className="block h-px w-12 bg-[#C5A059] mt-4 mb-6" />
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] font-medium max-w-3xl" data-testid="testimonials-title">
              {t.testimonialsPage.title}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
              {t.testimonialsPage.intro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 md:py-14 border-b border-black/5">
        <div className="container-narrow">
          <div className="flex items-center gap-2 mb-5">
            <Globe size={16} className="text-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#64748B] font-medium">
              {lang === "fr" ? "Filtrer par pays" : "Filter by country"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
              {t.testimonialsPage.filterAll} ({items.length})
            </FilterChip>
            {countries.map(([country, count]) => (
              <FilterChip
                key={country}
                active={filter === country}
                onClick={() => setFilter(country)}
                testid={`filter-${country.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {country} ({count})
              </FilterChip>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24">
        <div className="container-narrow">
          {loading ? (
            <div className="text-center text-[#64748B] py-12" data-testid="testimonials-loading">
              {lang === "fr" ? "Chargement..." : "Loading..."}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center bg-white rounded-2xl border border-black/5 py-16 px-6" data-testid="testimonials-empty">
              <Quote size={32} className="text-[#C5A059] mx-auto mb-4 opacity-50" />
              <p className="text-[#64748B]">{t.testimonialsPage.empty}</p>
            </div>
          ) : (
            <>
              <div className="text-sm text-[#64748B] mb-8" data-testid="testimonials-count">
                {t.testimonialsPage.countCount(filtered.length)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((item, i) => (
                  <motion.article
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                    className="bg-white rounded-2xl p-7 md:p-8 shadow-[0_4px_30px_-10px_rgba(0,43,73,0.08)] border border-black/5 flex flex-col hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,43,73,0.18)] transition-all"
                    data-testid={`public-testimonial-${i}`}
                  >
                    {item.image_url && (
                      <div className="w-16 h-16 rounded-full overflow-hidden mb-4 ring-2 ring-[#C5A059]/30">
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <Quote size={22} className="text-[#C5A059] mb-3 flex-shrink-0" />
                    <p className="font-serif text-lg text-[#0A192F] leading-relaxed flex-1 italic">
                      « {lang === "en" ? item.quote_en || item.quote_fr : item.quote_fr || item.quote_en} »
                    </p>
                    <div className="mt-6 pt-5 border-t border-black/10 flex items-end justify-between gap-3">
                      <div>
                        <div className="font-semibold text-[#002B49]">{item.name}</div>
                        <div className="text-sm text-[#64748B] mt-0.5">{item.location}</div>
                        {item.country && (
                          <div className="inline-block mt-2 text-[10px] uppercase tracking-[0.18em] text-[#C5A059] font-medium">
                            {item.country}
                          </div>
                        )}
                      </div>
                      <ShareButtons item={item} lang={lang} index={i} />
                    </div>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F3F0EA]">
        <div className="container-narrow text-center max-w-2xl mx-auto">
          <Heart size={32} className="text-[#C5A059] mx-auto mb-4" fill="#C5A059" />
          <h2 className="font-serif text-3xl md:text-5xl text-[#0A192F] leading-[1.1] font-medium" data-testid="testimonials-cta-title">
            {t.testimonialsPage.ctaTitle}
          </h2>
          <p className="text-[#64748B] text-base md:text-lg mt-4">{t.testimonialsPage.ctaSubtitle}</p>
          <button
            onClick={goToDonate}
            className="btn-primary mt-8"
            data-testid="testimonials-cta-donate"
          >
            {t.testimonialsPage.ctaButton}
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
};

const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
      active
        ? "bg-[#002B49] border-[#002B49] text-white"
        : "bg-white border-[#E5E7EB] text-[#334155] hover:border-[#002B49]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const ShareButtons = ({ item, lang, index }) => {
  const [copied, setCopied] = useState(false);
  const quote = lang === "en" ? item.quote_en || item.quote_fr : item.quote_fr || item.quote_en;
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${lang === "en" ? "/testimonials" : "/temoignages"}`
      : "";
  const text =
    lang === "fr"
      ? `« ${quote} » — ${item.name}, ${item.location}. Soutenez la Fondation Cœur Solidaire.`
      : `"${quote}" — ${item.name}, ${item.location}. Support Fondation Cœur Solidaire.`;
  const enc = encodeURIComponent(text);
  const encUrl = encodeURIComponent(shareUrl);

  const copyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`${text} ${shareUrl}`);
      setCopied(true);
      toast.success(lang === "fr" ? "Témoignage copié" : "Testimonial copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0" data-testid={`share-zone-${index}`}>
      <a
        href={`https://twitter.com/intent/tweet?text=${enc}&url=${encUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-8 h-8 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#334155] hover:bg-[#002B49] hover:text-white hover:border-[#002B49] transition-all"
        aria-label="Twitter"
        data-testid={`share-twitter-${index}`}
      >
        <Twitter size={13} />
      </a>
      <a
        href={`https://wa.me/?text=${enc}%20${encUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-8 h-8 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#334155] hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all"
        aria-label="WhatsApp"
        data-testid={`share-whatsapp-${index}`}
      >
        <Send size={13} />
      </a>
      <button
        onClick={copyLink}
        className="w-8 h-8 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#334155] hover:bg-[#C5A059] hover:text-white hover:border-[#C5A059] transition-all"
        aria-label="Copy link"
        data-testid={`share-copy-${index}`}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
};

export default TestimonialsPage;
